import { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';

const card = { background: '#111827', border: '1px solid #1E2D45', borderRadius: 8 };
const inp = { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 10px', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' };

const TEAM_GROUPS = [
  { key: '', label: 'Unassigned' },
  { key: 'headquarters', label: 'Headquarters' },
  { key: 'build', label: 'Build Team' },
  { key: 'growth', label: 'Growth Team' },
  { key: 'bd', label: 'BD Team' },
  { key: 'operations', label: 'Operations' },
  { key: 'other', label: 'Other' },
];

function RequestCard({ request, onResolved }) {
  const [team, setTeam] = useState('');
  const [busy, setBusy] = useState(false);

  const resolve = async (action) => {
    setBusy(true);
    try {
      const r = await crmService.resolveTeamRequest(request.id, { action, team_group: team || null });
      if (action === 'approve' && r.invite_url) {
        window.prompt(
          `Approved — member created and invitation ${r.email_sent ? 'emailed' : 'created (email unavailable)'}. Copy the link:`,
          r.invite_url
        );
      }
      onResolved();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const resolved = request.status !== 'pending';
  return (
    <div style={{ ...card, padding: '12px 16px', marginBottom: 8, opacity: resolved ? 0.55 : 1 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ color: '#F1F5F9', fontSize: 13, fontWeight: 600 }}>{request.name}</span>
        <span style={{ color: '#94A3B8', fontSize: 11 }}>{request.email}</span>
        <span style={{ color: '#4B5563', fontSize: 10 }}>requested by {request.requester_email || 'unknown'} · {new Date(request.created_at).toLocaleDateString()}</span>
        {resolved && (
          <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: request.status === 'approved' ? '#10B981' : '#6B7280', textTransform: 'uppercase' }}>
            {request.status}
          </span>
        )}
      </div>
      {request.reason && <div style={{ color: '#6B7280', fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>"{request.reason}"</div>}
      {!resolved && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={team} onChange={e => setTeam(e.target.value)} style={{ ...inp, width: 150 }}>
            {TEAM_GROUPS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
          <button onClick={() => resolve('approve')} disabled={busy}
            style={{ padding: '5px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
            Approve & Invite
          </button>
          <button onClick={() => resolve('decline')} disabled={busy}
            style={{ padding: '5px 14px', background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
            Decline
          </button>
        </div>
      )}
    </div>
  );
}

function NavigatorCard({ item, onResolved }) {
  const [answer, setAnswer] = useState(item.answer || '');
  const [busy, setBusy] = useState(false);

  const act = async (data) => {
    setBusy(true);
    try { await crmService.moderateNavigator(item.id, data); onResolved(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const isPending = item.status === 'pending';
  return (
    <div style={{ ...card, padding: '12px 16px', marginBottom: 8, borderLeft: `3px solid ${isPending ? '#c4a563' : '#22D3EE'}` }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontSize: 9, color: isPending ? '#c4a563' : '#22D3EE', fontWeight: 700, textTransform: 'uppercase', flexShrink: 0 }}>
          {isPending ? 'Awaiting review' : 'Approved — needs answer'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#4B5563' }}>{new Date(item.created_at).toLocaleDateString()} · anonymous</span>
      </div>
      <div style={{ color: '#F1F5F9', fontSize: 13, lineHeight: 1.6, margin: '8px 0' }}>{item.body}</div>
      <textarea
        value={answer} onChange={e => setAnswer(e.target.value)}
        placeholder="Headquarters' answer (posted with the question at the Watering Hole)…"
        style={{ ...inp, width: '100%', height: 56, resize: 'vertical' }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={() => act({ status: 'approved', answer: answer.trim() || undefined })} disabled={busy}
          style={{ padding: '5px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
          {answer.trim() ? (isPending ? 'Approve & Answer' : 'Post Answer') : 'Approve (no answer yet)'}
        </button>
        {isPending && (
          <button onClick={() => act({ status: 'rejected' })} disabled={busy}
            style={{ padding: '5px 14px', background: 'none', border: '1px solid #EF444444', color: '#EF4444', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
            Reject
          </button>
        )}
      </div>
    </div>
  );
}

export default function PersonalInbox() {
  const [requests, setRequests] = useState([]);
  const [navigator, setNavigator] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => Promise.all([
    crmService.getTeamRequests().then(setRequests),
    crmService.getNavigatorPending().then(setNavigator),
  ]).catch(console.error), []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  if (loading) return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 40 }}>Opening the inbox…</div>;

  const pendingReqs = requests.filter(r => r.status === 'pending');
  const resolvedReqs = requests.filter(r => r.status !== 'pending').slice(0, 5);

  return (
    <div style={{ padding: '16px 20px', maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div style={{ ...card, borderTop: `2px solid ${pendingReqs.length ? '#c4a563' : '#10B981'}`, padding: '12px 16px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Member Requests</div>
          <div style={{ color: '#F1F5F9', fontSize: 22, fontWeight: 700, marginTop: 4 }}>{pendingReqs.length}</div>
          <div style={{ color: '#4B5563', fontSize: 9, marginTop: 2 }}>awaiting decision</div>
        </div>
        <div style={{ ...card, borderTop: `2px solid ${navigator.length ? '#c4a563' : '#10B981'}`, padding: '12px 16px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Lead Navigator Queue</div>
          <div style={{ color: '#F1F5F9', fontSize: 22, fontWeight: 700, marginTop: 4 }}>{navigator.length}</div>
          <div style={{ color: '#4B5563', fontSize: 9, marginTop: 2 }}>to review or answer — the herd checks daily</div>
        </div>
      </div>

      <div style={{ color: '#94A3B8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Member Requests
      </div>
      {pendingReqs.length === 0 && <div style={{ color: '#374151', fontSize: 12, marginBottom: 12 }}>No pending requests.</div>}
      {pendingReqs.map(r => <RequestCard key={r.id} request={r} onResolved={load} />)}
      {resolvedReqs.length > 0 && (
        <>
          <div style={{ color: '#4B5563', fontSize: 10, margin: '10px 0 6px' }}>Recently resolved</div>
          {resolvedReqs.map(r => <RequestCard key={r.id} request={r} onResolved={load} />)}
        </>
      )}

      <div style={{ color: '#94A3B8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '20px 0 8px' }}>
        Lead Navigator — Review & Answer
      </div>
      <div style={{ color: '#4B5563', fontSize: 11, marginBottom: 10 }}>
        Submissions are anonymous — even here. Approved items post to the Watering Hole for the whole family.
      </div>
      {navigator.length === 0 && <div style={{ color: '#374151', fontSize: 12 }}>Queue is clear. The Navigator rests.</div>}
      {navigator.map(item => <NavigatorCard key={item.id} item={item} onResolved={load} />)}
    </div>
  );
}
